import { useCallback } from 'react';
import { MAX_BULK_MESSAGES } from '@/api/contract';
import { ClientErrorCodes } from '@/api/errors';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import type { BulkResult, Label } from '@/api/types';
import {
  bulkFailures,
  reportActionNotice,
  useOptimisticMessageMutation,
} from '@/hooks/useMessageMutations';

/**
 * "Label as" and a chip's × (plan/17 §5): ONE `POST /messages/labels` per
 * account — more only when the ids pass the bulk cap — with the labels to
 * add and to take off.
 *
 * It is an optimistic message mutation like a star: the chips change now, the
 * request follows, a refusal (thrown, or a partial `BulkResult`) restores the
 * cache and says why. Labels belong to one mailbox, so the variables name one
 * account; the picker never offers a batch that spans two.
 */
export interface ApplyLabelsVariables {
  accountId: string;
  /** Message ids: conversation rows already expanded to members + Sent. */
  messageIds: string[];
  add: Label[];
  remove: Label[];
}

/** What to say when labelling failed, in the words of the refusal. */
export function labelFailureCopy(code: string | undefined, fallback?: string): string {
  switch (code) {
    case 'labels_not_supported':
      return 'This folder can’t hold labels, so nothing was changed there.';
    case 'label_busy':
      return 'That label is being renamed or deleted; try again when it has finished.';
    case 'not_found':
      return 'That label or message is no longer there.';
    default:
      return fallback || 'The labels could not be changed, and have been put back.';
  }
}

function asBulk(answer: unknown): BulkResult {
  const result = (answer ?? {}) as Partial<BulkResult>;
  return {
    applied: Array.isArray(result.applied) ? result.applied : [],
    failed: Array.isArray(result.failed) ? result.failed : [],
  };
}

export function useApplyLabelsMutation() {
  const api = useApi();
  const mutation = useOptimisticMessageMutation<ApplyLabelsVariables>({
    scopeId: 'labels',
    mutationFn: async ({ accountId, messageIds, add, remove }) => {
      const merged: BulkResult = { applied: [], failed: [] };
      for (let start = 0; start < messageIds.length; start += MAX_BULK_MESSAGES) {
        const chunk = messageIds.slice(start, start + MAX_BULK_MESSAGES);
        try {
          const answer = asBulk(
            await api.labels.apply(accountId, {
              messageIds: chunk,
              add: add.map((label) => label.id),
              remove: remove.map((label) => label.id),
            }),
          );
          merged.applied.push(...answer.applied);
          merged.failed.push(...answer.failed);
        } catch (error) {
          // The first chunk refused is the whole verb refused; a later one
          // is reported per id, as the other bulk verbs do.
          if (start === 0) throw error;
          merged.failed.push(
            ...chunk.map((id) => ({
              id,
              code: error instanceof ApiError ? error.code : ClientErrorCodes.Unknown,
              message: error instanceof Error ? error.message : 'The operation failed.',
            })),
          );
        }
      }
      return merged;
    },
    patches: ({ accountId, messageIds, add, remove }) => [
      {
        kind: 'message-labels',
        accountId,
        messageIds,
        edit: {
          add: add.map((label) => label.keyword),
          // Taking a label off takes off its old keyword too (§4).
          remove: remove.flatMap((label) =>
            label.previousKeyword !== null ? [label.keyword, label.previousKeyword] : [label.keyword],
          ),
        },
      },
    ],
    failureLabel: (_variables, error) =>
      labelFailureCopy(error instanceof ApiError ? error.code : undefined, error instanceof ApiError ? error.message : undefined),
  });

  const { mutate } = mutation;
  /**
   * The verb. A partial refusal has been rolled back by the kit already; this
   * says which refusal it was, on the same snackbar every refused verb uses.
   */
  const apply = useCallback(
    (variables: ApplyLabelsVariables) => {
      if (variables.messageIds.length === 0) return;
      if (variables.add.length === 0 && variables.remove.length === 0) return;
      mutate(variables, {
        onSuccess: (data) => {
          const [first] = bulkFailures(data);
          if (first) reportActionNotice(labelFailureCopy(first.code, first.message));
        },
      });
    },
    [mutate],
  );
  return { ...mutation, apply };
}
